import { useState, useRef, useCallback, useEffect } from 'react';
import { Add as AddIcon, Remove as RemoveIcon } from '@mui/icons-material';
import { Alert, Box, IconButton, Typography } from '@mui/material';
import { TreeItem } from '@mui/x-tree-view';
import { useNavigate } from 'react-router-dom';
import { useDeleteNode, useSearchOntology } from '../../hooks/api/use-ontology.hook';
import { NodeApiModel } from '../../../../types/models/node.model';
import { getChildType, NodeType } from '../../../../types/constants/node-type';
import { useConfirm } from '../../contexts/confirm.context';
import { useUser } from '../../contexts/user.context';
import { Permission } from '../../../../types/constants/permission';
import { LoadingIndicator } from '../LoadingIndicator';

type Props = { node: NodeApiModel };

export const OntologyTreeNode = ({ node }: Props) => {
  const navigate = useNavigate();
  const [user] = useUser();
  const [showConfirm] = useConfirm();
  const [deleteNode, { error: deleteError }] = useDeleteNode();

  const [children, setChildren] = useState<NodeApiModel[]>([]);
  const [showLabelButtons, setShowLabelButtons] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const childType = getChildType(node.type);

  const { loading, error, response, fetchNextPage, hasNextPage } = useSearchOntology({
    parentId: node.id,
    nodeType: childType as NodeType,
  }, children.length > 0);

  useEffect(() => {
    if (response) {
      setChildren(response.pages.map((page) => page.result).flat());
    }
  }, [response]);

  const handleScroll = useCallback(() => {
    if (
      containerRef.current &&
      containerRef.current.scrollTop + containerRef.current.clientHeight >= containerRef.current.scrollHeight - 60
    ) {
      fetchNextPage();
    }
  }, [fetchNextPage]);

  const renderLabelButtons = () => {
    if (!user?.hasPermission(Permission.ONTOLOGY)) {
      return null;
    }

    return (
      <Box
        sx={{ display: 'flex', alignItems: 'center' }}
        onMouseEnter={() => setShowLabelButtons(true)}
        onMouseLeave={() => setShowLabelButtons(false)}
      >
        <Typography sx={{ flexGrow: 1 }}>
          {node.description ? `${node.description.replaceAll('_', ' ')} (${node.name})` : node.name}
        </Typography>
        <IconButton
          onClick={(e) => {
            navigate(`/ontology/${node.type}/${node.id}/add`);
            e.stopPropagation();
          }}
          // Disable adding new children for survey sub types since the current schema cant support it
          sx={{ visibility: showLabelButtons && node.type !== NodeType.SURVEY_SUB_TYPE ? 'inherit' : 'hidden' }}
        >
          <AddIcon />
        </IconButton>
        <IconButton
          onClick={(e) => {
            showConfirm({
              onConfirm: () => deleteNode({ nodeId: node.id, nodeType: node.type }),
              title: 'Delete Node',
              message: `Are you sure you want to delete node "${node.name}"?`,
              error: deleteError?.response?.data?.message,
            });
            e.stopPropagation();
          }}
          sx={{ visibility: showLabelButtons ? 'inherit' : 'hidden' }}
        >
          <RemoveIcon />
        </IconButton>
      </Box>
    );
  };

  return (
    <>
      <TreeItem
        itemId={`${node.id}-${node.type}`}
        label={renderLabelButtons()}
        onClick={() => {
          if (childType) {
            fetchNextPage();
          }
          navigate(`/ontology/${node.type}/${node.id}`);
        }}
      >
        <div ref={containerRef} onScroll={handleScroll} style={{ overflowY: 'auto', maxHeight: '400px' }}>
          {children.map((child) => (
            <OntologyTreeNode key={child.id} node={child} />
          ))}

          {error ? (
            <Alert sx={{ mt: '1rem' }} severity="error">
              {error?.response?.data.message}
            </Alert>
          ) : null}

          {(loading || (children.length === 0 && hasNextPage)) && (
            <TreeItem itemId={`${node.id}-${node.type}-loading`} label={<LoadingIndicator />} />
          )}
        </div>
      </TreeItem>
    </>
  );
};
